import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import * as attendanceApi from "../api/attendance";
import { formatDisplayDate } from "../utils/dateFormat";
import { generateCourseFeePeriods } from "../utils/feePeriods";
import { downloadMonthlyAttendancePdf } from "../utils/attendanceMonthlyPdf";
import { downloadMonthlyAttendanceCsv } from "../utils/attendanceCsv";
import "./AdminAttendance.css";

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function periodKey(period) {
  return `${period.year}-${period.month}`;
}

function pickDefaultPeriod(periods) {
  if (!periods.length) return null;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const exact = periods.find(
    (p) => p.year === currentYear && p.month === currentMonth,
  );
  if (exact) return exact;

  const currentIndex = currentYear * 12 + currentMonth;
  let best = periods[0];
  for (const period of periods) {
    const idx = period.year * 12 + period.month;
    if (idx <= currentIndex) best = period;
    else break;
  }
  return best;
}

function periodBounds(periods) {
  if (!periods.length) return { min: undefined, max: undefined };
  const first = periods[0];
  const last = periods[periods.length - 1];
  const min = `${first.year}-${String(first.month).padStart(2, "0")}-01`;
  const lastDay = new Date(last.year, last.month, 0).getDate();
  const max = `${last.year}-${String(last.month).padStart(2, "0")}-${String(
    lastDay,
  ).padStart(2, "0")}`;
  return { min, max };
}

export default function AdminAttendanceCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const notificationCtx = useContext(NotificationContext);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { hasPermission, loading: permLoading } = useViewPermission(
    "view_attendance",
  );
  const { hasPermission: canEdit } = useViewPermission("edit_attendance");

  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [takeDate, setTakeDate] = useState(todayIso());
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("");
  const [periodsReady, setPeriodsReady] = useState(false);

  const periods = useMemo(
    () => (course ? generateCourseFeePeriods(course) : []),
    [course],
  );

  const selectedPeriod = useMemo(() => {
    if (!periods.length) return null;
    return (
      periods.find((p) => periodKey(p) === selectedPeriodKey) || periods[0]
    );
  }, [periods, selectedPeriodKey]);

  const dateBounds = useMemo(() => periodBounds(periods), [periods]);

  // Load course + sessions for selected filter period
  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const month = selectedPeriod?.month;
        const year = selectedPeriod?.year;
        // First pass: load course only (no unfiltered session flash).
        // Second pass: load sessions for the chosen month.
        const res = await attendanceApi.listSessions({
          courseId: Number(courseId),
          ...(periodsReady && month && year ? { year, month } : {}),
        });
        const nextCourse = res.data.course;
        setCourse(nextCourse);

        if (!periodsReady && nextCourse) {
          setSessions([]);
          const coursePeriods = generateCourseFeePeriods(nextCourse);
          const def = pickDefaultPeriod(coursePeriods);
          if (def) setSelectedPeriodKey(periodKey(def));
          setPeriodsReady(true);
        } else {
          setSessions(res.data.sessions || []);
        }
      } catch (err) {
        console.error("Error loading sessions:", err);
        setCourse(null);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    if (!periodsReady || selectedPeriod) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, courseId, selectedPeriodKey, periodsReady]);

  // Keep take-date inside the course running window when periods load.
  useEffect(() => {
    if (!dateBounds.min || !dateBounds.max) return;
    setTakeDate((prev) => {
      if (prev < dateBounds.min) return dateBounds.min;
      if (prev > dateBounds.max) return dateBounds.max;
      return prev;
    });
  }, [dateBounds.min, dateBounds.max]);

  const fetchMonthlyMatrix = async () => {
    const res = await attendanceApi.monthlyMatrix({
      courseId: Number(courseId),
      year: selectedPeriod.year,
      month: selectedPeriod.month,
    });
    const data = res.data || {};
    if (!data.dates?.length) {
      const err = new Error("No attendance sessions to export for this month.");
      err.stat = "empty";
      throw err;
    }
    return data;
  };

  const handleExportMonthlyPdf = async () => {
    if (!selectedPeriod || exporting || exportingCsv || sessions.length === 0) {
      return;
    }
    try {
      setExporting(true);
      const data = await fetchMonthlyMatrix();
      await downloadMonthlyAttendancePdf({
        courseTitle: data.course?.title || course?.title,
        periodLabel: selectedPeriod.label,
        year: data.year || selectedPeriod.year,
        month: data.month || selectedPeriod.month,
        dates: data.dates || [],
        students: data.students || [],
      });
    } catch (err) {
      console.error("Monthly attendance PDF export failed:", err);
      notificationCtx.show({
        message:
          err?.stat === "empty"
            ? err.message
            : err?.response?.data?.message ||
              "Failed to export monthly attendance PDF.",
        stat: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportMonthlyCsv = async () => {
    if (!selectedPeriod || exporting || exportingCsv || sessions.length === 0) {
      return;
    }
    try {
      setExportingCsv(true);
      const data = await fetchMonthlyMatrix();
      downloadMonthlyAttendanceCsv({
        courseTitle: data.course?.title || course?.title,
        year: data.year || selectedPeriod.year,
        month: data.month || selectedPeriod.month,
        dates: data.dates || [],
        students: data.students || [],
      });
    } catch (err) {
      console.error("Monthly attendance CSV export failed:", err);
      notificationCtx.show({
        message:
          err?.stat === "empty"
            ? err.message
            : err?.response?.data?.message ||
              "Failed to export monthly attendance CSV.",
        stat: "error",
      });
    } finally {
      setExportingCsv(false);
    }
  };

  if (authLoading || permLoading || (loading && !course)) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <Navbar />
        <div className="att-shell">
          <div className="att-empty">
            <div className="att-empty__title">Course not found</div>
            <div className="att-empty__hint" style={{ marginBottom: 14 }}>
              This course may be unavailable for your attendance scope.
            </div>
            <Link to="/admin/attendance" className="att-btn att-btn--ghost">
              Back to attendance
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="att-shell">
        <div className="att-banner">
          <div className="att-banner__row">
            <div>
              <p className="att-banner__crumb">
                <Link to="/admin/attendance">Attendance courses</Link>
                {" / "}
                Sessions
              </p>
              <h1 className="att-banner__title">{course.title}</h1>
              <p className="att-banner__subtitle">
                {course.Teacher?.name || "Teacher TBA"}
                {course.year ? ` · ${course.year}` : ""}
                {!course.attendanceEnabled ? " · Attendance off (review only)" : ""}
              </p>
            </div>

            <div className="att-banner__controls">
              {canEdit && course.attendanceEnabled ? (
                <div className="att-banner__control-group">
                  <div className="att-banner__control-label">Take attendance</div>
                  <div className="att-banner__actions">
                    <input
                      className="att-field"
                      type="date"
                      value={takeDate}
                      min={dateBounds.min}
                      max={dateBounds.max}
                      onChange={(e) => setTakeDate(e.target.value)}
                      aria-label="Attendance date"
                    />
                    <button
                      type="button"
                      className="att-btn att-btn--mint"
                      onClick={() =>
                        navigate(
                          `/admin/attendance/${courseId}/take/${takeDate}`,
                        )
                      }
                    >
                      Open take session
                    </button>
                  </div>
                </div>
              ) : null}
              {canEdit && !course.attendanceEnabled ? (
                <div className="att-banner__control-group">
                  <div className="att-banner__control-label">Take attendance</div>
                  <span className="att-banner__control-hint">
                    Re-enable attendance on the Courses screen to take new
                    sessions. Saved records below can still be reviewed.
                  </span>
                </div>
              ) : null}

              <div className="att-banner__control-group">
                <div className="att-banner__control-label">
                  Review saved records
                </div>
                <div className="att-banner__actions">
                  {periods.length === 0 ? (
                    <span className="att-banner__control-hint">
                      No session months configured
                    </span>
                  ) : (
                    <>
                      <select
                        className="att-field"
                        value={selectedPeriod ? periodKey(selectedPeriod) : ""}
                        onChange={(e) => setSelectedPeriodKey(e.target.value)}
                        aria-label="Course month"
                      >
                        {periods.map((period) => (
                          <option
                            key={periodKey(period)}
                            value={periodKey(period)}
                          >
                            {period.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="att-btn att-btn--lavender"
                        disabled={
                          exporting ||
                          exportingCsv ||
                          loading ||
                          sessions.length === 0
                        }
                        onClick={handleExportMonthlyPdf}
                      >
                        {exporting ? "Exporting…" : "Export monthly PDF"}
                      </button>
                      <button
                        type="button"
                        className="att-btn att-btn--lavender"
                        disabled={
                          exporting ||
                          exportingCsv ||
                          loading ||
                          sessions.length === 0
                        }
                        onClick={handleExportMonthlyCsv}
                      >
                        {exportingCsv ? "Exporting…" : "Export monthly CSV"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="att-spacer" />

        {loading ? (
          <div className="att-empty">
            <div className="att-empty__hint">Loading records…</div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="att-empty">
            <div className="att-empty__title">No records this month</div>
            <div className="att-empty__hint">
              No attendance saved for{" "}
              {selectedPeriod?.label || "the selected month"}.
            </div>
          </div>
        ) : (
          <div className="att-table-wrap">
            <div className="att-table-scroll">
              <table className="table align-middle mb-0 att-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>PRESENT</th>
                    <th>ABSENT</th>
                    <th>TOTAL</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td style={{ fontWeight: 600 }}>
                        {formatDisplayDate(session.sessionDate)}
                      </td>
                      <td>{session.counts?.present ?? 0}</td>
                      <td>{session.counts?.absent ?? 0}</td>
                      <td>{session.counts?.total ?? 0}</td>
                      <td>
                        <button
                          type="button"
                          className="att-btn att-btn--lavender"
                          onClick={() =>
                            navigate(
                              `/admin/attendance/${courseId}/sessions/${session.id}`,
                            )
                          }
                        >
                          {canEdit ? "View / edit" : "View"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
